import { useEffect, useState } from 'react';

import Heading from 'components/partials/heading/heading';
import Toggle from 'components/partials/toggle/toggle';
import File from 'containers/admin/clients/client/tabs-components/files-tab/file/file';
import TableComponent from 'components/partials/table-component/table-component';
import Loader from 'components/partials/loader/loader';
import Button from 'components/partials/button/button';

import toast from 'components/partials/toast/toast';
import { renderModal } from 'helpers/modal-render';

import { getFileDetailsActionMenuItems } from 'containers/admin/clients/client/tabs-components/files-tab/file/file-action-menu-items';
import { getFileUsersTableSettings } from 'containers/admin/clients/client/tabs-components/files-tab/file/file-page/file-page-table-settings';

import { useClient } from 'hooks/api/use-clients';
import { useCurrentUser } from 'state/current-user/current-user.thunk';
import {
  useFile,
  useFileUsers,
  useUpdateFile,
  usePatchFileUsers,
} from 'hooks/api/use-files';

import { transformFileToFormObject } from 'helpers/forms';

import { ALERTS } from 'containers/admin/clients/client/tabs-components/files-tab/file/file.constants';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';

import type { FileUserRow } from 'containers/admin/clients/client/tabs-components/files-tab/file/file-page/file-page-table-settings';
import type { FileData } from 'containers/admin/clients/client/tabs-components/files-tab/file/file';
import type {
  AssetClientUserUpdateRequest,
  UpdateFileArgs,
} from 'api/market-view-api';

interface FilePageProps {
  clientId: string | number | undefined;
  fileId: string | number | undefined;
  notificationParams?: { heading?: string; variant?: string };
}

interface UserAccessChange {
  userId: string | undefined;
  hasAccess: boolean;
}

let newUsersAssignedToFile: FileUserRow[] = [];

const FilePage = ({ clientId, fileId }: FilePageProps): React.JSX.Element => {
  const [isFormDisabled, setIsFormDisabled] = useState<boolean>(true);
  const [displayedData, setDisplayedData] = useState<FileUserRow[]>([]);
  const [isToggleOn, setIsToggleOn] = useState<boolean>(false);
  const [isAllUsersSelected, setIsAllUsersSelected] = useState<boolean>(false);
  const [isShareBtnDisabled, setIsShareBtnDisabled] = useState<boolean>(false);

  const currentUser = useCurrentUser();
  const { data: currentClient, loading: clientLoading } = useClient(clientId);
  const currentUserData = currentUser?.data as
    | { oktaUserId?: string; fdAnalyst?: boolean }
    | null
    | undefined;

  const userId = currentUserData?.oktaUserId ?? '';

  const { data: fileData, isLoading: isFileLoading } = useFile({
    userId,
    clientId: clientId ?? '',
    fileId: fileId ?? '',
    enabled: !!userId && !!clientId && !!fileId,
  });

  const { data: fileUsersData, isLoading: isFileUsersLoading } = useFileUsers({
    userId,
    clientId: clientId ?? '',
    fileId: fileId ?? '',
    enabled: !!userId && !!clientId && !!fileId,
  });

  const updateFileMutation = useUpdateFile();
  const patchFileUsersMutation = usePatchFileUsers();

  const formLoading = updateFileMutation.isPending;

  useEffect(() => {
    if (fileData) {
      setIsToggleOn(
        !!(fileData as FileData & { autoAssignToUsers?: boolean })
          ?.autoAssignToUsers
      );
    }
  }, [fileData]);

  useEffect(() => {
    if (fileUsersData) {
      setDisplayedData(fileUsersData as FileUserRow[]);
      setIsAllUsersSelected(
        fileUsersData.every((user) => user.hasAccess === true)
      );
      setIsShareBtnDisabled(true);
      newUsersAssignedToFile = [];
    }
  }, [fileUsersData]);

  const onFileEdit = (data: Record<string, unknown>): void => {
    if (!clientId || !fileId || !currentUser.data) return;

    const status =
      ((data as { status?: string }).status as string | undefined) ||
      ((data as { alertStatus?: string }).alertStatus as string | undefined) ||
      'ACTIVE';

    updateFileMutation.mutate(
      {
        currentUser: { data: currentUser.data },
        clientId,
        fileId,
        ...(data as Pick<
          UpdateFileArgs,
          'fileName' | 'file' | 'description' | 'status' | 'autoAssignToUsers'
        >),
      },
      {
        onSuccess: (updated) => {
          setIsFormDisabled(true);
          toast[ALERTS[status].variant]({
            title: ALERTS[status].heading,
          });
          setIsToggleOn(
            !!(updated as FileData & { autoAssignToUsers?: boolean })
              .autoAssignToUsers
          );
        },
        onError: (error) =>
          toast.error({
            title: 'Error during updating file',
            message:
              (error as { response?: { data?: { detail?: string } } })?.response
                ?.data?.detail ||
              `We couldn't update the file. Please try again or contact support.`,
          }),
      }
    );
  };

  const onToggleChange = (toggled: boolean): void => {
    const data = {
      autoAssignToUsers: toggled,
      alertStatus: 'UPDATED',
    };

    renderModal({
      headerText: `${
        toggled ? 'A' : 'Una'
      }ssign this file to all new users of ${currentClient?.name} ?`,
      text: `All new users for ${currentClient?.name} will ${
        toggled ? '' : 'NOT'
      } have access to this document automatically.`,
      onConfirmCallback: () => onFileEdit(data),
      onConfirmButtonLabel: `Yes, ${
        toggled ? '' : 'un'
      }assign to all new users`,
    });
  };

  const onSelectAllUsers = ({ hasAccess }: UserAccessChange): void => {
    newUsersAssignedToFile = displayedData.map((item) => ({
      ...item,
      userId: item.userId,
      hasAccess,
    }));
    setDisplayedData(newUsersAssignedToFile);
    setIsShareBtnDisabled(false);
    setIsAllUsersSelected(hasAccess);
  };

  const onUserChange = (changedUser: UserAccessChange): void => {
    if (isAllUsersSelected) {
      setIsAllUsersSelected(false);
    }

    const user = newUsersAssignedToFile.find(
      (item) => String(item.userId) === changedUser.userId
    );

    if (user) {
      user.hasAccess = changedUser.hasAccess;
    } else {
      newUsersAssignedToFile.push({
        userId: changedUser.userId ?? '',
        hasAccess: changedUser.hasAccess,
      });
    }

    const newUsersAccesses = displayedData.map((item) =>
      String(item.userId) === changedUser.userId
        ? { ...item, hasAccess: changedUser.hasAccess }
        : { ...item }
    );

    setDisplayedData(newUsersAccesses);
    if (newUsersAccesses.every((u) => u.hasAccess === true)) {
      setIsAllUsersSelected(true);
    }
    setIsShareBtnDisabled(false);
  };

  const onUsersChangesSubmit = (): void => {
    if (!clientId || !fileId || !currentUser.data) return;

    const usersFromServer = (fileUsersData ?? []) as FileUserRow[];

    const changedUsers = newUsersAssignedToFile.filter((item) =>
      usersFromServer.find(
        (user) =>
          Number(user.userId) === Number(item.userId) &&
          user.hasAccess !== item.hasAccess
      )
    );

    const changedUserRequests: AssetClientUserUpdateRequest[] =
      changedUsers.map((user) => ({
        userId: user.userId,
        hasAccess: !!user.hasAccess,
      }));

    patchFileUsersMutation.mutate(
      {
        currentUser: { data: currentUser.data },
        clientId,
        fileId,
        changedUsers: changedUserRequests,
      },
      {
        onSuccess: () => {
          newUsersAssignedToFile = [];
          setIsShareBtnDisabled(true);
          toast.success({ title: ALERTS.UPDATED.heading });
        },
        onError: (e) =>
          console.error('Error during updating file.fileUsers', e),
      }
    );
  };

  const onUsersChangesCancel = (): void => {
    const usersFromServer = (fileUsersData ?? []) as FileUserRow[];

    setDisplayedData(usersFromServer);

    setIsAllUsersSelected(
      usersFromServer.every((user) => user.hasAccess === true)
    );

    setIsShareBtnDisabled(true);
    newUsersAssignedToFile = [];
  };

  return (
    <>
      {clientLoading ||
      isFileLoading ||
      isFileUsersLoading ||
      !currentClient?.name ||
      !(fileData as FileData | undefined)?.name ? (
        <Loader loading={clientLoading || isFileLoading || isFileUsersLoading} />
      ) : (
        <ResponsiveWrapper>
          <File
            pageName={(fileData as FileData)?.name}
            fileData={fileData as FileData}
            formLoading={formLoading}
            isFormDisabled={isFormDisabled}
            setIsFormDisabled={setIsFormDisabled}
            onSubmit={onFileEdit}
            actionMenuItems={getFileDetailsActionMenuItems({
              fileName: (fileData as FileData)?.name,
              clientName: currentClient?.name,
              onActionAplied: onFileEdit,
              status: (fileData as FileData | undefined)?.status,
            })}
            defaultValues={transformFileToFormObject(fileData as never)}
          />
          <div className="w-full">
            {(fileData as FileData | undefined)?.status !== 'ARCHIVED' &&
              !currentUserData?.fdAnalyst && (
                <div>
                  <div className="flex items-center justify-between mt-5">
                    <Heading
                      variant="h3"
                      customClass="my-3"
                      data-testid="file-is-available-to-heading"
                    >
                      File is available to
                    </Heading>

                    {!!displayedData.length && (
                      <Toggle
                        onToggleChange={onToggleChange}
                        isToggleOn={isToggleOn}
                        toggleText="Assign to all new users"
                        data-testid="file-page-toggle"
                      />
                    )}
                  </div>
                  {displayedData.length > 0 ? (
                    <TableComponent
                      columns={getFileUsersTableSettings({
                        onUserChange,
                        onSelectAllUsers,
                        isAllUsersSelected,
                      })}
                      data={displayedData}
                      isLoading={clientLoading}
                      showingRecordsTextIsOn={false}
                      viewByAmountSelectIsOn={false}
                      pageIndex={0}
                      pageSize={displayedData.length || 10}
                      totalRecords={displayedData.length}
                      sortingParams={[]}
                      setSortingParams={() => {}}
                    />
                  ) : (
                    <div className="text-md">
                      {!clientLoading && 'No users for this client were found'}
                    </div>
                  )}
                  {!!displayedData.length && (
                    <div className="flex justify-end mt-6">
                      <Button
                        disabled={isShareBtnDisabled}
                        variant="reserve-text"
                        size="md"
                        onClick={onUsersChangesCancel}
                        type="button"
                        data-testid="cancel-file-users-button"
                      >
                        Cancel
                      </Button>
                      <Button
                        disabled={isShareBtnDisabled}
                        variant="brand-filled"
                        size="md"
                        type="submit"
                        onClick={() => onUsersChangesSubmit()}
                        data-testid="update-file-users-button"
                      >
                        Update access
                      </Button>
                    </div>
                  )}
                </div>
              )}
          </div>
        </ResponsiveWrapper>
      )}
    </>
  );
};

export default FilePage;
